import { useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import AttachmentMenu from "./AttachmentMenu";
import VoiceRecorder from "./VoiceRecorder";
import type { LocationData } from "@/hooks/useWhatsAppChat";

interface ChatInputProps {
  onSend: (text: string) => Promise<boolean>;
  onSendImage: (base64: string, filename: string, caption?: string) => Promise<boolean>;
  onSendFile: (base64: string, filename: string, caption?: string) => Promise<boolean>;
  onSendVoice: (base64Ptt: string) => Promise<boolean>;
  onSendLocation: (location: LocationData) => Promise<boolean>;
  onSendLink: (url: string, caption?: string) => Promise<boolean>;
  sending: boolean;
  disabled?: boolean;
  aiReplying?: boolean;
}

type InputMode = "text" | "voice" | "location" | "link";

export default function ChatInput({
  onSend,
  onSendImage,
  onSendFile,
  onSendVoice,
  onSendLocation,
  onSendLink,
  sending,
  disabled,
  aiReplying,
}: ChatInputProps) {
  const { t } = useTranslation("dashboard");
  const [text, setText] = useState("");
  const [showAttachMenu, setShowAttachMenu] = useState(false);
  const [inputMode, setInputMode] = useState<InputMode>("text");

  // Location state
  const [locTitle, setLocTitle] = useState("");
  const [locAddress, setLocAddress] = useState("");
  const [locLat, setLocLat] = useState("");
  const [locLng, setLocLng] = useState("");

  // Link state
  const [linkUrl, setLinkUrl] = useState("");
  const [linkCaption, setLinkCaption] = useState("");

  const inputRef = useRef<HTMLTextAreaElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const resetMode = useCallback(() => {
    setInputMode("text");
    setLocTitle("");
    setLocAddress("");
    setLocLat("");
    setLocLng("");
    setLinkUrl("");
    setLinkCaption("");
    inputRef.current?.focus();
  }, []);

  const handleSend = useCallback(async () => {
    if (!text.trim() || sending) return;
    const ok = await onSend(text);
    if (ok) {
      setText("");
      inputRef.current?.focus();
    }
  }, [text, sending, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const adjustHeight = useCallback(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 100)}px`;
  }, []);

  const readFileAsBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        resolve(result);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleImageSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await readFileAsBase64(file);
      await onSendImage(base64, file.name);
    } catch {
      // silently fail
    }
    if (imageInputRef.current) imageInputRef.current.value = "";
  }, [onSendImage]);

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const base64 = await readFileAsBase64(file);
      await onSendFile(base64, file.name);
    } catch {
      // silently fail
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, [onSendFile]);

  const handleSendLocation = useCallback(async () => {
    if (!locTitle || !locLat || !locLng || sending) return;
    const ok = await onSendLocation({
      lat: locLat,
      lng: locLng,
      title: locTitle,
      address: locAddress,
    });
    if (ok) resetMode();
  }, [locTitle, locLat, locLng, locAddress, sending, onSendLocation, resetMode]);

  const handleSendLink = useCallback(async () => {
    if (!linkUrl || sending) return;
    const ok = await onSendLink(linkUrl, linkCaption);
    if (ok) resetMode();
  }, [linkUrl, linkCaption, sending, onSendLink, resetMode]);

  const handleVoiceSend = useCallback(async (base64Ptt: string) => {
    const ok = await onSendVoice(base64Ptt);
    if (ok) resetMode();
  }, [onSendVoice, resetMode]);

  // --- Render: AI Replying ---
  if (aiReplying) {
    return (
      <div className="flex-shrink-0 px-3 py-2 bg-background-50 border-t border-background-200/60">
        <div className="bg-secondary-50 rounded-xl border border-secondary-200/60 px-4 py-3 flex items-center gap-3">
          <div className="w-9 h-9 rounded-full bg-secondary-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-secondary-500 text-base animate-pulse"></i>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-secondary-700">{t("chatInput.aiReplying")}</p>
            <div className="flex items-center gap-1 mt-1">
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 animate-bounce" style={{ animationDelay: "0ms" }}></span>
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 animate-bounce" style={{ animationDelay: "150ms" }}></span>
              <span className="w-1.5 h-1.5 rounded-full bg-secondary-400 animate-bounce" style={{ animationDelay: "300ms" }}></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- Render: Location mode ---
  if (inputMode === "location") {
    return (
      <div className="flex-shrink-0 px-3 py-2 bg-background-50 border-t border-background-200/60">
        <div className="bg-background-100 rounded-xl border border-background-200/50 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-green-100 text-green-600 flex-shrink-0">
              <i className="ri-map-pin-line text-sm"></i>
            </span>
            <span className="text-sm font-medium text-foreground-700">{t("chatInput.shareLocation")}</span>
            <button
              onClick={resetMode}
              className="ml-auto w-6 h-6 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-400 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <input
              value={locTitle}
              onChange={(e) => setLocTitle(e.target.value)}
              placeholder={t("chatInput.locationName")}
              className="col-span-2 px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
            />
            <input
              value={locAddress}
              onChange={(e) => setLocAddress(e.target.value)}
              placeholder={t("chatInput.address")}
              className="col-span-2 px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
            />
            <input
              value={locLat}
              onChange={(e) => setLocLat(e.target.value)}
              placeholder={t("chatInput.latitude")}
              className="px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
            />
            <input
              value={locLng}
              onChange={(e) => setLocLng(e.target.value)}
              placeholder={t("chatInput.longitude")}
              className="px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
            />
          </div>
          <button
            onClick={handleSendLocation}
            disabled={!locTitle || !locLat || !locLng || sending}
            className="w-full py-1.5 text-sm font-medium rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:bg-background-200 disabled:text-foreground-400 cursor-pointer transition-colors whitespace-nowrap"
          >
            {sending ? t("chatInput.sending") : t("chatInput.sendLocation")}
          </button>
        </div>
      </div>
    );
  }

  // --- Render: Link mode ---
  if (inputMode === "link") {
    return (
      <div className="flex-shrink-0 px-3 py-2 bg-background-50 border-t border-background-200/60">
        <div className="bg-background-100 rounded-xl border border-background-200/50 p-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="w-7 h-7 flex items-center justify-center rounded-full bg-orange-100 text-orange-600 flex-shrink-0">
              <i className="ri-link text-sm"></i>
            </span>
            <span className="text-sm font-medium text-foreground-700">{t("chatInput.shareLink")}</span>
            <button
              onClick={resetMode}
              className="ml-auto w-6 h-6 flex items-center justify-center rounded-full hover:bg-background-200 text-foreground-400 cursor-pointer transition-colors"
            >
              <i className="ri-close-line text-sm"></i>
            </button>
          </div>
          <input
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder={t("chatInput.url")}
            className="w-full px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
          />
          <input
            value={linkCaption}
            onChange={(e) => setLinkCaption(e.target.value)}
            placeholder={t("chatInput.captionOptional")}
            className="w-full px-3 py-1.5 text-sm bg-background-50 border border-background-200/60 rounded-lg outline-none focus:border-secondary-400 text-foreground-900 placeholder:text-foreground-400"
          />
          <button
            onClick={handleSendLink}
            disabled={!linkUrl || sending}
            className="w-full py-1.5 text-sm font-medium rounded-lg bg-accent-500 text-white hover:bg-accent-600 disabled:bg-background-200 disabled:text-foreground-400 cursor-pointer transition-colors whitespace-nowrap"
          >
            {sending ? t("chatInput.sending") : t("chatInput.sendLink")}
          </button>
        </div>
      </div>
    );
  }

  // --- Render: Text/Voice mode ---
  const isTextMode = inputMode === "text";
  const showSendBtn = text.trim().length > 0 || sending;

  return (
    <div className="flex-shrink-0 px-3 py-2 bg-background-50 border-t border-background-200/60 relative">
      <div className="flex items-end gap-2 bg-background-100 rounded-xl px-2 py-1.5 border border-background-200/50 focus-within:border-secondary-400 focus-within:ring-1 focus-within:ring-secondary-300 transition-colors">
        {isTextMode && (
          <>
            {!showSendBtn && (
              <button
                type="button"
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-600 hover:bg-background-200/50 cursor-pointer transition-colors flex-shrink-0"
                onClick={() => setInputMode("voice")}
                title={t("chatInput.recordVoice")}
              >
                <i className="ri-mic-line text-lg"></i>
              </button>
            )}

            <div className="relative">
              <button
                type="button"
                onClick={() => setShowAttachMenu(!showAttachMenu)}
                className="w-9 h-9 flex items-center justify-center rounded-full text-foreground-400 hover:text-foreground-600 hover:bg-background-200/50 cursor-pointer transition-colors flex-shrink-0"
                title={t("chatInput.attach")}
              >
                <i className="ri-add-line text-lg"></i>
              </button>
              <AttachmentMenu
                isOpen={showAttachMenu}
                onClose={() => setShowAttachMenu(false)}
                onSelectImage={() => imageInputRef.current?.click()}
                onSelectFile={() => fileInputRef.current?.click()}
                onSelectLocation={() => { setInputMode("location"); setShowAttachMenu(false); }}
                onSelectLink={() => { setInputMode("link"); setShowAttachMenu(false); }}
              />
            </div>

            <textarea
              ref={inputRef}
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                adjustHeight();
              }}
              onKeyDown={handleKeyDown}
              placeholder={t("chatInput.typeMessage")}
              rows={1}
              disabled={disabled}
              className="flex-1 bg-transparent text-sm text-foreground-900 placeholder:text-foreground-400 resize-none outline-none py-1.5 max-h-[100px] disabled:opacity-50"
            />

            {showSendBtn && (
              <button
                onClick={handleSend}
                disabled={!text.trim() || sending || disabled}
                className="w-9 h-9 flex items-center justify-center rounded-full text-white bg-accent-500 hover:bg-accent-600 disabled:bg-background-200 disabled:text-foreground-400 cursor-pointer transition-colors flex-shrink-0"
              >
                {sending ? (
                  <i className="ri-loader-4-line animate-spin text-sm"></i>
                ) : (
                  <i className="ri-send-plane-fill text-sm"></i>
                )}
              </button>
            )}
          </>
        )}

        {inputMode === "voice" && (
          <div className="w-full">
            <VoiceRecorder
              onSend={handleVoiceSend}
              onCancel={resetMode}
            />
          </div>
        )}
      </div>

      {/* Hidden file inputs */}
      <input
        ref={imageInputRef}
        type="file"
        accept="image/*"
        onChange={handleImageSelect}
        className="hidden"
      />
      <input
        ref={fileInputRef}
        type="file"
        accept="*/*"
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}